import type { BoxProps } from '@chakra-ui/react'
import { Box, Flex, Grid, Image, Select, Text } from '@chakra-ui/react'
import type { OfferAddon } from 'BrokerTsLib/src/dtos'
import { useCallback, useContext, useEffect } from 'react'

import {
  FormSection,
  FormSectionBody,
  FormSectionHeader,
  FormSectionTitle,
} from '../../components/FormSection'
import { addonOffersContext } from '../../context/OfferAddonContext'
import { useGetAddonProductEnums } from '../../hooks'
import { getLogo } from '../../utils/companiesLogos'

interface RecapTableRowProps extends BoxProps {
  image?: React.ReactNode
  addonOffer: OfferAddon
}

interface ExtraInsuranceInfoProps {
  label: string
  value: string | { current: string; options: string[] }
  isEditable?: boolean
}

export const ExtraInsuranceInfo = ({
  label,
  value,
  isEditable = false,
}: ExtraInsuranceInfoProps) => {
  return (
    <Flex sx={{ alignItems: 'center', fontSize: 'sm' }}>
      <Text sx={{ w: '50%', px: 0, py: 1, pr: 4 }}>{label}</Text>
      <Box sx={{ w: '50%', px: 0, py: 1 }}>
        {typeof value === 'string' && (
          <Text as="span" sx={{ fontWeight: 'medium' }}>
            {value}
          </Text>
        )}

        {!isEditable && typeof value === 'object' && (
          <Text as="span" sx={{ fontWeight: 'medium' }}>
            {value.current}
          </Text>
        )}

        {isEditable && typeof value === 'object' && (
          <Select value={value.current} size="sm" sx={{ w: '100%' }}>
            {value.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        )}
      </Box>
    </Flex>
  )
}

export const RecapExtraInsuranceTableRow = ({
  image,
  addonOffer,
  sx }: RecapTableRowProps) => {

  const { mutateAsync: loadEnums, data: dataEnums } = useGetAddonProductEnums()
  useEffect(() => {
    loadEnums({
      offerId: addonOffer.offerId,
      riskVariantId: addonOffer.riskVariantId,
      changedParams: [],
      filterOutput: false
    })
  }, [])

  const getParamTextValues = useCallback((name: string, value: string) => {
    const paramEnum = dataEnums?.data.find(x => x.paramName == name)
    const paramVal = paramEnum?.values.find(x => x.value == value)

    return {
      name: paramEnum?.paramDescription,
      value: paramVal?.text
    }
  }, [dataEnums])

  return (
    <Grid>
      {image && image}

      <Flex
        sx={{
          w: '100%',
          px: 2,
          py: 1,
          justifyContent: 'space-between',
          borderRadius: 'md',
          bg: 'blue.50',
        }}
      >
        {addonOffer.name && <Text sx={{ fontWeight: 'medium' }}>{addonOffer.name}</Text>}

        {addonOffer.priceFrequency && (
          <Box
            sx={{
              fontWeight: 'semibold',
              textAlign: 'end',
              verticalAlign: 'top',
              ...sx,
            }}
          >
            {`Splátka: ${addonOffer.priceFrequencyText}`}
          </Box>
        )}
      </Flex>

      <Grid sx={{ w: '100%', gap: 1, m: 0, p: 0, gridTemplateColumns: '1fr 1fr', alignItems: 'start' }}>
        {addonOffer.params?.parameterValues.map(x => {
          const publicTexts = getParamTextValues(x.name, x.value)
          return (
            <ExtraInsuranceInfo key={x.name} label={publicTexts.name ?? ''} value={publicTexts.value ?? ''} />)
        })}
      </Grid>
    </Grid>
  )
}

export const RecapExtraInsurance = () => {
  const { addonOffers } = useContext(addonOffersContext)

  if (addonOffers.length == 0)
    return null

  return (
    <FormSection>
      <FormSectionHeader>
        <FormSectionTitle>Rekapitulace doplňkového pojištění</FormSectionTitle>
      </FormSectionHeader>

      <FormSectionBody>
        {addonOffers.map(ao => (
          <Grid key={ao.offerId} sx={{ w: '100%' }}>
            <RecapExtraInsuranceTableRow
              image={<Image src={getLogo(ao.insurerId)} alt="Fortegra" width="64px" maxH="48px" />}
              addonOffer={ao}
            />
          </Grid>
        ))}
      </FormSectionBody>
    </FormSection>
  )
}